import Axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import {
  getClientPlatform,
  X_CLIENT_PLATFORM_HEADER,
} from '../utils/react-native';

const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL;

// withCredentials: 서버가 Set-Cookie 로 내려주는 쿠키(예: 마켓 기구매 쿠키 uglyus_market_purchased)를
// 브라우저가 저장하고 이후 요청에 함께 전송하도록 자격증명 모드로 호출한다.
// cross-origin(웹 ↔ api 서브도메인) 응답의 쿠키 저장에 필수이며, 서버 CORS는 credentials 허용 + 특정 origin 이어야 한다.
export const axios = Axios.create({ baseURL, withCredentials: true });

/**
 * 모든 공유 인스턴스 요청에 클라이언트 환경 식별 헤더(X-Client-Platform)를 부착한다.
 * 앱 웹뷰면 APP, 그 외는 WEB. _getHeader()를 우회하는 호출(예: photo presigned)도 공통 커버한다.
 */
axios.interceptors.request.use((config: AxiosRequestConfig) => {
  config.headers = {
    ...config.headers,
    [X_CLIENT_PLATFORM_HEADER]: getClientPlatform(),
  };
  return config;
});

const _getHeader = (config?: AxiosRequestConfig | undefined) => {
  if (typeof window === 'undefined') {
    return { ...(config && config) };
  }

  return {
    ...(config && config),
    headers: {
      Authorization: 'Bearer ' + window.localStorage.getItem('token'),
    },
  };
};

export const get = <T extends unknown = any>(
  url: string,
  config?: AxiosRequestConfig | undefined,
): Promise<AxiosResponse<T>> => {
  const options = _getHeader(config);
  return axios.get(url, options);
};

export const post = <T extends unknown = any>(
  url: string,
  data: any,
  options?: any,
): Promise<AxiosResponse<T>> => {
  const baseHeaders = _getHeader()['headers'];
  const extraHeaders = options?.['headers'] ?? {};
  const headers = { ...baseHeaders, ...extraHeaders };
  return axios.post(url, data, { ...options, headers });
};

export const deleteCall = <T extends unknown = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig | undefined,
) => {
  return axios.delete<T>(url, { ..._getHeader(options), data });
};

export const patch = <T extends unknown = any>(
  url: string,
  data: any,
  options?: AxiosRequestConfig | undefined,
) => {
  const baseHeaders = _getHeader()['headers'];
  const extraHeaders = options?.['headers'] ?? {};
  const headers = { ...baseHeaders, ...extraHeaders };
  return axios.patch<T>(url, data, { ...options, headers });
};

export const put = <T extends unknown = any>(
  url: string,
  data: any,
  options?: AxiosRequestConfig | undefined,
) => {
  const baseHeaders = _getHeader()['headers'];
  const extraHeaders = options?.['headers'] ?? {};
  const headers = { ...baseHeaders, ...extraHeaders };
  return axios.put<T>(url, data, { ...options, headers });
};
